/**
 * The folder panel's silhouette: the rail TILE and the PANEL beside it as ONE
 * shape, joined at the tile's right edge with concave fillets — so the panel
 * reads as growing out of the tile rather than as a card floating next to it.
 * (Sileo's island toast, turned on its side: their header pill sits ABOVE the
 * body; our tile sits to the LEFT of the panel.)
 *
 *          ┌──────────────────────────┐      ← panel, radius R
 *    ╭─────┤                          │
 *    │tile ╯   ← concave fillet r     │
 *    │     ╮                          │
 *    ╰─────┤                          │
 *          └──────────────────────────┘
 *
 * Pure functions, so the geometry is unit-tested and the component only
 * paints. Coordinates are LOCAL to the popup's box: x from the tile's left
 * edge, y from the panel's top.
 */

export interface IslandGeometry {
  /** The box: `width` = tile + panel width, `height` = panel height. */
  width: number
  height: number
  /** The tile is `tile` square at x ∈ [0, tile], y ∈ [tileTop, tileTop + tile]. */
  tile: number
  tileTop: number
  /** The tile's outer (left) corner radius, the panel's corners, the fillets. */
  tileRadius: number
  panelRadius: number
  filletRadius: number
}

/** The smallest `tileTop` (and the smallest room below the tile) that keeps
 *  a fillet and a panel corner from overlapping. */
export const islandMinTileInset = (g: Pick<IslandGeometry, 'panelRadius' | 'filletRadius'>) => g.panelRadius + g.filletRadius

/** SVG path data for the silhouette, traced clockwise from the panel's
 *  top-left corner. Convex corners sweep one way, the two fillets the other. */
export function islandPath(g: IslandGeometry): string {
  const { width: W, height: H, tile: T, tileTop: ty, tileRadius: rt, panelRadius: R, filletRadius: r } = g
  const n = (v: number) => Math.round(v * 100) / 100
  return [
    `M ${n(T + R)} 0`,
    `H ${n(W - R)}`, `A ${R} ${R} 0 0 1 ${n(W)} ${R}`,
    `V ${n(H - R)}`, `A ${R} ${R} 0 0 1 ${n(W - R)} ${n(H)}`,
    `H ${n(T + R)}`, `A ${R} ${R} 0 0 1 ${n(T)} ${n(H - R)}`,
    // up the panel's left edge to just below the tile, then the lower fillet
    // curving INTO the tile's bottom edge
    `V ${n(ty + T + r)}`, `A ${r} ${r} 0 0 0 ${n(T - r)} ${n(ty + T)}`,
    // along the tile's bottom edge, around its two outer corners, along its top
    `H ${n(rt)}`, `A ${rt} ${rt} 0 0 1 0 ${n(ty + T - rt)}`,
    `V ${n(ty + rt)}`, `A ${rt} ${rt} 0 0 1 ${n(rt)} ${n(ty)}`,
    `H ${n(T - r)}`,
    // the upper fillet, back onto the panel's left edge, up to its top corner
    `A ${r} ${r} 0 0 0 ${n(T)} ${n(ty - r)}`,
    `V ${R}`, `A ${R} ${R} 0 0 1 ${n(T + R)} 0`,
    'Z',
  ].join(' ')
}

export interface IslandLayoutInput {
  /** The rail tile's viewport rect. */
  tileRect: { left: number; top: number; width: number; height: number }
  /** The panel's content size (beside the tile). */
  panelWidth: number
  panelHeight: number
  viewportHeight: number
  /** Keep the box at least this far from the viewport's top and bottom. */
  margin: number
  tileRadius: number
  panelRadius: number
  filletRadius: number
}

export interface IslandLayout extends IslandGeometry {
  /** Where the box sits in the viewport. */
  left: number
  top: number
}

/**
 * Place the box so the tile sits VERTICALLY CENTRED on the panel when there is
 * room, sliding the panel up or down to stay inside the viewport otherwise —
 * the tile never moves (it is the rail's), the panel moves around it. The
 * tile's inset is then clamped so the fillets never collide with the panel's
 * corners; on a very short viewport that can push the box past the margin,
 * which is the lesser evil: a broken silhouette is worse than an edge.
 */
export function islandLayout(i: IslandLayoutInput): IslandLayout {
  const tile = i.tileRect.height
  const height = Math.max(i.panelHeight, tile + 2 * islandMinTileInset(i))
  const minInset = islandMinTileInset(i)
  let top = i.tileRect.top - (height - tile) / 2
  top = Math.min(top, i.viewportHeight - i.margin - height)
  top = Math.max(top, i.margin)
  let tileTop = i.tileRect.top - top
  tileTop = Math.max(minInset, Math.min(height - tile - minInset, tileTop))
  top = i.tileRect.top - tileTop
  return {
    left: i.tileRect.left,
    top,
    width: tile + i.panelWidth,
    height,
    tile,
    tileTop,
    tileRadius: i.tileRadius,
    panelRadius: i.panelRadius,
    filletRadius: i.filletRadius,
  }
}

/**
 * The silhouette PART-WAY OPEN — the island as it grows out of its tile.
 *
 * `p` runs 0 → 1: at 0 the shape is exactly the tile, at 1 it is `final`. The
 * TILE NEVER MOVES (the box's `top` compensates for the changing inset), which
 * is the whole point — the panel is what grows, out of a folder that stays
 * where the pointer left it.
 *
 * Every radius is clamped to what the partial box can actually hold. An
 * unclamped corner is what makes a morph flip inside out in its first frames:
 * the panel is only a few pixels wide there, and a 12px corner radius on it
 * traces backwards.
 */
export function islandMorph(final: IslandLayout, p: number): IslandLayout {
  const t = Math.max(0, Math.min(1, p))
  const tile = final.tile
  const width = tile + (final.width - tile) * t
  const height = tile + (final.height - tile) * t
  const tileTop = final.tileTop * t
  const panelWidth = width - tile
  const panelRadius = Math.max(0, Math.min(final.panelRadius, panelWidth / 2, height / 2))
  // A fillet can be no deeper than the room above or below the tile, and no
  // wider than the panel it curves into.
  const filletRadius = Math.max(0, Math.min(
    final.filletRadius * t, tileTop, height - tileTop - tile, panelWidth,
  ))
  return {
    ...final,
    width,
    height,
    tileTop,
    panelRadius,
    filletRadius,
    // tile's viewport y = top + tileTop, and it is invariant.
    top: final.top + final.tileTop - tileTop,
  }
}
